import React, {useEffect} from "react";
import Head from 'next/head';
import {useRouter} from 'next/router';
// @material-ui/core components
import {makeStyles} from 'tss-react/mui';
import {client} from "../../../../request/client";
import useSWR, {SWRConfig} from "swr";

// @material-ui/icons
// core components
import Header from "../../../../components/Header/Header.js";
import Footer from "../../../../components/Footer/Footer.js";
import HeaderLinks from "../../../../components/Header/HeaderLinks.js";
import logo from "../../../../public/img/logo.png";

import styles from "../../../../styles/jss/nextjs-material-kit/pages/articleStyle.js";
import ArticleDetails from "../../../../pages-sections/ArticleDetails";
import PreFooter from "../../../../pages-sections/LandingPage-Sections/PreFooter.js";
import Parallax from "../../../../components/Parallax/Parallax";
import {buildSlug} from "../../../../util/url";
import JSONSchemaData from "../../../../components/JSONSchemaData/JSONSchemaData";
import {GPTProvider, GPTSlot} from "goopubtag";
import {useArticlesTrackRequest} from "../../../../request/api";

const dashboardRoutes = [];

const useStyles = makeStyles()(styles);

function ArticleDetailPage(props) {
  const { classes, cx } = useStyles();
  const { articleId, ...rest } = props;
  const router = useRouter();

  const { data, error, isLoading } = useSWR(`/v1/articles/${articleId}`);

  const { trigger: trackArticle } = useArticlesTrackRequest({ articleId });

  useEffect(() => {
    void trackArticle();
  }, []);

  let article = null;
  let articlePageTitle = 'Loading...';
  if (data) {
    article = data;
    articlePageTitle = `${article.meta_title || article.title}`;
  }

  const jsonSchemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article?.title,
    "image": article?.image_uri,
    "description": article?.meta_description || article?.content_snippet.replace(/<[^>]*>?/gm, ''),
  };

  const jsonBreadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://successatschool.org/"
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Advice",
      "item": "https://successatschool.org/advice"
    }, {
      "@type": "ListItem",
      "position": 3,
      "name": article?.category,
      "item": `https://successatschool.org/advice/${buildSlug(article?.category)}`
    }, {
      "@type": "ListItem",
      "position": 4,
      "name": article?.title,
      "item": `https://successatschool.org/advice/${buildSlug(article?.category)}/${buildSlug(article?.title)}/${article?.id}`
    }]};

  return (
    <div>
      <Head>
        <title>
          {articlePageTitle}
        </title>
        {!!article?.meta_description && <>
          <meta
            name="description"
            content={article?.meta_description}
            key="desc"
          />
        </>}
        {!!article?.meta_keywords && <>
          <meta
            name="keywords"
            content={article?.meta_keywords}
            key="keywords"
          />
        </>}
        {!!article && <>
          <link rel="canonical" href={`https://successatschool.org/${article.type === 'news' ? 'news' : 'advice'}/${buildSlug(article.category)}/${buildSlug(article.title)}/${article.id}`} />
        </>}
      </Head>
      <Header
        color="transparent"
        routes={dashboardRoutes}
        logo={logo}
        rightLinks={<HeaderLinks />}
        fixed
        changeColorOnScroll={{
          height: 400,
          color: "white",
        }}
        {...rest}
      />
      <Parallax
        className={"removeArrow parallaxx-compact"}
        filter
        image={article?.header_uri ||article?.image_uri || "/img/faces/advice_slide_bg.png"}
      ></Parallax>

      <div
        className={cx(
          classes.main,
          classes.detailSection,
        )}
      >
        <div className={classes.section}>
          <div className={classes.container}>
            <div
              style={{
                padding: "0px 0 50px",
              }}
              className={cx(classes.white_bg, "colored_bgg")}
            >
              <GPTProvider networkId={108480305}>
                <GPTSlot slotId="job-listing-ad" adUnit="Leaderboard_Advice" sizes={[728, 90]} fallback="expand" className={classes.advert} />
              </GPTProvider>

              {(isLoading)
                ? <strong>Loading...</strong>
                : <ArticleDetails article={article} />
              }
            </div>
          </div>
        </div>
      </div>

      <div className={cx(classes.darkBlue, "footer", "colored_bgg")}>
        <PreFooter />
      </div>
      <Footer />
      <JSONSchemaData key_name={'breadcrumbSchema'} data={jsonBreadcrumbData} />
      <JSONSchemaData key_name={'ArticleSchema'} data={jsonSchemaData} />
    </div>
  );
}

export default function ArticleDetailPageSwr({ fallback, ...rest }) {
  return (
    <SWRConfig value={{ fallback }}>
      <ArticleDetailPage {...rest} />
    </SWRConfig>
  );
}

export async function getServerSideProps({ query }) {
  const { articleId, category } = query;

  try {
    const data = await client({
      url: `/v1/articles/${articleId}`,
      method: "get",
      headers: {},
    }).then((res) => res.data);

    if (category !== buildSlug(data.category)) {
      return {
        redirect: {
          destination: `/advice/${buildSlug(data.category)}/${buildSlug(data.title)}/${articleId}`,
          permanent: true,
        },
      };
    }

    return {
      props: {
        fallback: {
          [`/v1/articles/${articleId}`]: data,
        },
        articleId,
      }
    };
  }
  catch (e) {
    if (e.response && e.response.status === 404) {
      return {
        notFound: true,
      };
    }
    else {
      throw e;
    }
  }
}
